import { Observable } from 'rxjs';
import { singletonFactory } from '@warehouse/shared/util';
import { brokerFetchClientAdapter } from '@warehouse/object-browser/infra';
import {
	BrokerJob,
	BrokerJobsRepository,
	brokerJobsQueryKey,
	WatchJobsForNxObjectParams,
	WatchJobsResult,
	SubmitJobParams,
	SubmitJobResult,
	BrokerJobDetails,
	GetJobParams,
	PING_SERVICE_UUID,
	TenantJobSearchParams,
	TenantJobSearchResult,
} from '../core';
import { brokerServicesRepositorySingleton } from './broker-services.repository.json';
import { BrokerJobAdapter } from './broker-job.adapter';
import { TenantJobSearchRequestBuilder } from './tenant-job-search-request.builder';

export class BrokerJobsRepositoryHttp implements BrokerJobsRepository {
	#brokerFetchClientAdapter = brokerFetchClientAdapter;

	watchJobsForNxObject({ nxObjectUuid }: WatchJobsForNxObjectParams): Observable<WatchJobsResult> {
		return this.#brokerFetchClientAdapter.watchObservableQuery({
			queryKey: brokerJobsQueryKey(nxObjectUuid),
			queryFn: () => this.searchJobs({ nxObjectUuid }),
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
		});
	}

	async submitJob({ nxObjectUuid, serviceUUID }: SubmitJobParams): Promise<SubmitJobResult> {
		const { error, data } = await this.#brokerFetchClientAdapter.client.POST('/jobs', {
			body: {
				serviceUUID,
				references: [{ 
					nxObjectUuid 
				}],
			},
		});

		if (error || !data) throw new Error(error?.error ?? 'Error submitting the job');
		return data;
	}

	async getJob({ jobId }: GetJobParams): Promise<BrokerJobDetails> {
		const { error, data } = await this.#brokerFetchClientAdapter.client.GET('/jobs/{jobId}', {
			params: { path: { jobId } },
		});

		if (error || !data) throw new Error(error?.error ?? 'Error fetching the job');
		return BrokerJobAdapter.adaptDetails(data);
	}

	async searchJobs({ nxObjectUuid }: { nxObjectUuid: string }): Promise<BrokerJob[]> {

		const { error, data } = await this.#brokerFetchClientAdapter.client.POST('/jobs/search', {
			body: {
				filters: [
					{ field: "nxObjectUuid", contains: nxObjectUuid },
					{ field: "serviceUUID",  doesNotEqual: [PING_SERVICE_UUID] }
				],
				page: 1,
				perPage: 50,
			},
		});

		if (error || !data) throw new Error('Error searching jobs');

		const servicesByUuid = new Map(
			brokerServicesRepositorySingleton.get()
				.getAvailableServicesConfig()
				.map((service) => [service.serviceUUID, service]),
		);
		return BrokerJobAdapter.adaptArray(data.jobs, servicesByUuid);
	}

	async searchTenantJobs(params: TenantJobSearchParams): Promise<TenantJobSearchResult> {
		const { error, data } = await this.#brokerFetchClientAdapter.client.POST('/jobs/search', {
			body: TenantJobSearchRequestBuilder.build(params),
		});

		if (error || !data) throw new Error('Error searching jobs');

		const servicesByUuid = new Map(
			brokerServicesRepositorySingleton.get()
				.getAvailableServicesConfig()
				.map((service) => [service.serviceUUID, service]),
		);
		return {
			jobs: BrokerJobAdapter.adaptArray(data.jobs, servicesByUuid),
			totalJobs: data.totalJobs,
			page: data.page,
			perPage: data.perPage,
		};
	}

	async cancelJob(jobId: string): Promise<void> {
		const { error } = await this.#brokerFetchClientAdapter.client.POST('/jobs/{jobId}/cancel', {
			params: {
				path: { jobId },
			},
		});

		if (error) throw new Error(error.error ?? 'Error cancelling job');
	}
}

export const brokerJobsRepositorySingleton = singletonFactory<BrokerJobsRepository>({
	factory: () => new BrokerJobsRepositoryHttp(),
});
